// ---------------------------------------------------------------------------
// TyPlayer — core player UNICO su Video.js 10 (@videojs/html, custom
// elements, skin stock). Caricato sia dall'app (index.html) sia dalla
// pagina embed (embed/index.html, path ../js/player-core.js).
//
// Factory:
//   var player = TyPlayer.create({
//       container: '#slot' | Element,   // dove montare il player
//       id: 'teyutoPlayer',             // id del media element
//       src: 'https://.../master.m3u8', // opzionale (omesso = nessuna sorgente)
//       type: 'application/x-mpegURL',  // MIME della sorgente
//       title: '...', poster: '...',
//       adTag: 'https://...vast...',    // ads IMA (SDK google.ima vanilla)
//       subtitles: [{ src, srclang, label, kind, default }], // tracce esterne
//       opts: {
//           autoplay, muted, controls, loop,
//           preview,   // preset trailer: autoplay muto in loop senza UI
//           fillsize,  // object-fit cover (via --media-object-fit, API del
//                      // media element v10)
//           live,      // hint stream-type="live"
//           skinClass, // classe di layout sull'elemento skin (glue di pagina)
//           // accettati per contratto ma NO-OP con la skin stock v10 (la
//           // skin include già quality/audio/sottotitoli/velocità nel menu
//           // impostazioni, fullscreen, PiP, tastiera e gesture touch e non
//           // è configurabile per-bottone):
//           qualityMenu, playbackRates, pip, fullscreenToggle
//       }
//   });
//
// Markup montato (stock):
//   <video-player>
//     <video-skin>                                  ← solo con controls
//       <hlsjs-video|native-hls-video src="...">
//         <video slot="media" playsinline></video>  ← nativo, in LIGHT DOM
//       </hlsjs-video>
//     </video-skin>
//   </video-player>
//
// Il <video slot="media"> (pattern supportato da custom-media-element) resta
// raggiungibile dal DOM: drawImage del glow ambientale, MediaSession,
// $(iframe).contents().find('video') dei moduli web sull'embed.
//
// La factory ritorna l'ADAPTER: la superficie in stile video.js 8 usata da
// tutto il codice esistente (currentTime() come funzione, .on/.off/.one,
// .play/.pause/.paused, .muted()/.volume()/.loop()/.poster(), .src(),
// .dispose(), .el(), textTracks(), fullscreen) mappata sul media element
// v10, che implementa l'interfaccia standard HTMLMediaElement.
//
// Inclusi nel core (oltre alla factory):
// - Ads IMA vanilla (setupImaAds): integrazione diretta con google.ima
//   (ima3.js dal CDN) senza plugin v8 — pre-roll al primo play, pausa/
//   ripresa del contenuto su CONTENT_PAUSE/RESUME_REQUESTED, e in ogni
//   caso di errore o fine ads il contenuto riparte SEMPRE.
// - Up-next (TyPlayer.showUpNext): card overlay applicativa stile YouTube
//   (countdown circolare, titolo/thumbnail, "Riproduci ora"/"Annulla")
//   sopra il player. NON è una customizzazione della skin: i controlli
//   restano stock v10, l'overlay è un layer nostro slottato nel player.
//
// Chromecast: NON sta nel core — l'app lo aggancia dall'esterno con
// bindPlayerToCast (js/cast.js), che replica sul ricevitore play/pause/seek
// e la traccia sottotitoli usando la superficie dell'adapter.
//
// TODO(v10): AirPlay custom, TeyutoPlayerAnalytics, VR, vtt-thumbnails,
// trimmer — rimossi con la migrazione, da ricostruire su questo core
// (vedi i marker TODO(v10) nei singoli file).
// ---------------------------------------------------------------------------

var TyPlayer = (function () {

    // HLS nativo: da preferire SOLO su WebKit Apple (iOS/iPadOS, dove su
    // iPhone hls.js non ha MSE, e Safari desktop).
    function isAppleWebKit() {
        var ua = navigator.userAgent || '';
        if (/iPad|iPhone|iPod/.test(ua)) { return true; }
        // iPadOS in "desktop mode" si dichiara Macintosh
        if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) { return true; }
        return /Safari/.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|Edg|Android/.test(ua);
    }

    function nativeHlsDeclared() {
        var probe = document.createElement('video');
        return !!(probe.canPlayType('application/vnd.apple.mpegurl') || probe.canPlayType('application/x-mpegURL'));
    }

    // hls.js gira solo con MSE (o ManagedMediaSource su iOS 17.1+)
    function hlsJsSupported() {
        var MS = window.ManagedMediaSource || window.MediaSource;
        try {
            return !!(MS && typeof MS.isTypeSupported === 'function' &&
                MS.isTypeSupported('video/mp4;codecs="avc1.42E01E,mp4a.40.2"'));
        } catch (e) { return false; }
    }

    // Sceglie il tag media v10 adatto alla sorgente, replicando il
    // comportamento di VHS in v8 (overrideNative ovunque tranne Safari):
    // HLS nativo su WebKit Apple, hls.js altrove. Sorgenti non-HLS → <video>.
    //
    // ATTENZIONE: canPlayType NON basta a decidere. Chromium su Android
    // (Chrome e la WebView di Capacitor) risponde "maybe" all'HLS e lo
    // riproduce col media stack nativo, ma NON espone le tracce testuali
    // in-manifest: il video parte e il menu sottotitoli resta vuoto (nessun
    // bottone CC). Per questo su Android va forzato hls.js, che le tracce
    // le costruisce lui (vedi il workaround NON_NATIVE_TEXT_TRACKS_FOUND).
    function mediaTagForSource(type, src) {
        var t = (type || '').toLowerCase();
        var s = (src || '').toLowerCase();
        var isHls = t.indexOf('mpegurl') !== -1 || s.indexOf('.m3u8') !== -1 || (!t && !s);
        if (!isHls) {
            return 'video';
        }
        if (isAppleWebKit() && nativeHlsDeclared()) {
            return 'native-hls-video';
        }
        if (hlsJsSupported()) {
            return 'hlsjs-video';
        }
        // niente MSE: ultima spiaggia il nativo, se dichiarato supportato
        return nativeHlsDeclared() ? 'native-hls-video' : 'hlsjs-video';
    }

    // Raggiunge il <video> nativo di un media element v10: prima lo slotted
    // in light DOM, poi lo shadow root (aperto) come fallback. Con un
    // <video> normale restituisce l'elemento stesso.
    function innerVideoEl(mediaEl) {
        if (!mediaEl) { return null; }
        if (mediaEl.tagName === 'VIDEO') { return mediaEl; }
        try {
            var v = mediaEl.querySelector(':scope > [slot=media]') || mediaEl.querySelector('video');
            if (v) { return v; }
        } catch (e) {}
        try {
            return mediaEl.shadowRoot ? mediaEl.shadowRoot.querySelector('video') : null;
        } catch (e) {}
        return null;
    }

    // Adapter con la superficie v8 usata dai call site esistenti.
    function adapter(mediaEl, wrapperEl) {
        // eventi "pointer" legati al wrapper: i tap sui controlli della skin
        // retargettano sull'host della skin, non sul media element
        var POINTER_EVENTS = { touchstart: 1, touchend: 1, touchmove: 1, click: 1 };
        // registro listener per supportare off('evento') senza fn (stile v8)
        var bag = {};

        function targetFor(ev) {
            return (POINTER_EVENTS[ev] && wrapperEl) ? wrapperEl : mediaEl;
        }

        var api = {
            __isTyAdapter: true,
            mediaEl: mediaEl,

            el: function () { return wrapperEl || mediaEl; },

            // currentTime(x): se i metadata non sono ancora pronti il seek
            // viene accodato a loadedmetadata (resume position affidabile)
            currentTime: function (t) {
                if (t === undefined) { return mediaEl.currentTime || 0; }
                var target = Number(t) || 0; // currentTime(false) → 0 (replay trailer)
                if (mediaEl.readyState >= 1) {
                    mediaEl.currentTime = target;
                } else {
                    var once = function () {
                        mediaEl.removeEventListener('loadedmetadata', once);
                        try { mediaEl.currentTime = target; } catch (e) {}
                    };
                    mediaEl.addEventListener('loadedmetadata', once);
                }
                return api;
            },

            duration: function () { return mediaEl.duration; },
            paused: function () { return mediaEl.paused; },
            ended: function () { return mediaEl.ended; },
            muted: function (m) {
                if (m === undefined) { return mediaEl.muted; }
                mediaEl.muted = !!m;
                return api;
            },
            volume: function (v) {
                if (v === undefined) { return mediaEl.volume; }
                mediaEl.volume = v;
                return api;
            },
            loop: function (l) {
                if (l === undefined) { return mediaEl.loop; }
                mediaEl.loop = !!l;
                return api;
            },
            poster: function (p) {
                if (p === undefined) { return mediaEl.getAttribute('poster'); }
                mediaEl.setAttribute('poster', p);
                return api;
            },

            play: function () {
                var p;
                try { p = mediaEl.play(); } catch (e) {}
                return (p && typeof p.catch === 'function') ? p : Promise.resolve();
            },
            pause: function () {
                try { mediaEl.pause(); } catch (e) {}
                return api;
            },

            // src({type, src}) oppure src('url'); senza argomenti fa da getter
            src: function (s) {
                if (s === undefined) { return mediaEl.getAttribute('src'); }
                var url = (typeof s === 'string') ? s : (s && s.src) || '';
                mediaEl.setAttribute('src', url);
                return api;
            },
            currentSrc: function () {
                return mediaEl.currentSrc || mediaEl.getAttribute('src') || '';
            },

            // controls(false): i controlli della skin v10 vivono nello shadow
            // DOM, si disattivano via pointer-events sul wrapper (ereditato
            // da tutto il sottoalbero)
            controls: function (c) {
                var el = api.el();
                if (c === undefined) { return el.style.pointerEvents !== 'none'; }
                el.style.pointerEvents = c ? '' : 'none';
                return api;
            },

            textTracks: function () { return mediaEl.textTracks; },

            // fullscreen (superficie usata da teyutoClientSdk)
            isFullscreen: function () {
                var fsEl = document.fullscreenElement || document.webkitFullscreenElement;
                return !!(fsEl && (fsEl === mediaEl || (api.el().contains && api.el().contains(fsEl)) || (fsEl.contains && fsEl.contains(mediaEl))));
            },
            requestFullscreen: function () {
                try {
                    if (typeof mediaEl.requestFullscreen === 'function') { return mediaEl.requestFullscreen(); }
                } catch (e) {}
                // iOS: fullscreen nativo del <video> interno
                try {
                    var v = innerVideoEl(mediaEl);
                    if (v && typeof v.webkitEnterFullscreen === 'function') { v.webkitEnterFullscreen(); }
                } catch (e) {}
            },
            exitFullscreen: function () {
                try {
                    if (document.exitFullscreen) { return document.exitFullscreen(); }
                    if (document.webkitExitFullscreen) { return document.webkitExitFullscreen(); }
                } catch (e) {}
            },

            on: function (ev, fn) {
                if (ev === 'ready') { // v8 ready → i custom element sono già pronti
                    setTimeout(function () { fn.call(api); }, 0);
                    return api;
                }
                var wrapped = function (e) { return fn.call(api, e); };
                (bag[ev] = bag[ev] || []).push({ fn: fn, wrapped: wrapped });
                targetFor(ev).addEventListener(ev, wrapped);
                return api;
            },

            one: function (ev, fn) {
                var once = function (e) {
                    api.off(ev, once);
                    return fn.call(api, e);
                };
                return api.on(ev, once);
            },

            // off('evento') rimuove tutti i listener registrati per quell'evento;
            // off('evento', fn) solo quello specifico
            off: function (ev, fn) {
                var list = bag[ev] || [];
                var keep = [];
                for (var i = 0; i < list.length; i++) {
                    if (fn === undefined || list[i].fn === fn) {
                        targetFor(ev).removeEventListener(ev, list[i].wrapped);
                    } else {
                        keep.push(list[i]);
                    }
                }
                bag[ev] = keep;
                return api;
            },

            // dispose: stop, sgancio listener e rimozione dal DOM. La classe
            // base dei custom element v10 esegue il destroy (teardown engine
            // hls.js) quando l'elemento resta scollegato dal DOM.
            dispose: function () {
                try { mediaEl.pause(); } catch (e) {}
                for (var ev in bag) {
                    api.off(ev);
                }
                try { mediaEl.removeAttribute('src'); } catch (e) {}
                var root = api.el();
                try {
                    if (root && root.parentNode) { root.parentNode.removeChild(root); }
                } catch (e) {}
                try { if (typeof mediaEl.destroy === 'function') { mediaEl.destroy(); } } catch (e) {}
                return api;
            }
        };

        return api;
    }

    // Stili dei layer applicativi del core (ads + up-next), iniettati una
    // volta sola. CSS minimale e dedicato: nessun riuso di classi legacy,
    // nessun override della skin stock.
    function injectCoreStyles() {
        if (document.getElementById('ty-player-core-style')) { return; }
        var style = document.createElement('style');
        style.id = 'ty-player-core-style';
        style.textContent = [
            /* contenitore ads IMA sovrapposto al video */
            '.ty-ad-container{position:absolute;inset:0;z-index:20;pointer-events:none;}',
            '.ty-ad-container.ty-ad-active{pointer-events:auto;}',
            /* banner visivo della fascia bassa: gradiente + label "Annuncio".
               pointer-events:none → i bottoni IMA (skip/learn more) restano
               cliccabili sopra di esso */
            /* gradiente estetico della fascia bassa (la UI vera — badge
               Annuncio, countdown, learn more, skip — è quella NATIVA di
               IMA, abilitata via AdsRenderingSettings.uiElements) */
            '.ty-ad-banner{position:absolute;left:0;right:0;bottom:0;height:96px;z-index:21;pointer-events:none;display:none;background:linear-gradient(to top, rgba(0,0,0,.72), transparent);}',
            '.ty-ad-container.ty-ad-active .ty-ad-banner{display:block;}',
            /* bottone play GRANDE al centro: usato per l\'ad in pausa E per il
               contenuto quando l\'autoplay viene bloccato dal browser */
            '.ty-ad-resume,.ty-bigplay{position:absolute;inset:0;z-index:25;display:none;align-items:center;justify-content:center;background:rgba(0,0,0,.35);border:0;cursor:pointer;padding:0;}',
            '.ty-ad-container.ty-ad-paused .ty-ad-resume{display:flex;}',
            '.ty-bigplay.ty-bigplay-visible{display:flex;}',
            /* stesso linguaggio della barra controlli della skin: pillola
               scura arrotondata "glass", niente cerchio bordato */
            '.ty-ad-resume span,.ty-bigplay span{width:104px;height:68px;border-radius:28px;background:rgba(18,18,24,.55);border:0;display:flex;align-items:center;justify-content:center;box-shadow:0 12px 36px rgba(0,0,0,.45);backdrop-filter:blur(18px) saturate(1.3);-webkit-backdrop-filter:blur(18px) saturate(1.3);transition:transform .15s ease,background .15s ease;}',
            '.ty-ad-resume:hover span,.ty-bigplay:hover span{transform:scale(1.06);background:rgba(24,24,28,.92);}',
            '.ty-ad-resume span::before,.ty-bigplay span::before{content:"";display:block;width:0;height:0;border-style:solid;border-width:14px 0 14px 23px;border-color:transparent transparent transparent #fff;margin-left:5px;}',
            /* popup in-player (skip / link / testo): chip "vetro" animate */
            '.ty-popup-layer{position:absolute;inset:0;z-index:15;pointer-events:none;overflow:hidden;}',
            '.ty-popup{position:absolute;pointer-events:auto;display:inline-flex;align-items:center;gap:8px;max-width:min(340px,80%);padding:10px 16px;border-radius:999px;background:rgba(18,18,20,.82);border:1px solid rgba(255,255,255,.22);color:#fff;font-size:13.5px;font-weight:600;backdrop-filter:blur(6px);box-shadow:0 8px 26px rgba(0,0,0,.45);opacity:0;transform:translateY(8px);transition:opacity .25s ease,transform .25s ease;}',
            '.ty-popup.ty-popup-visible{opacity:1;transform:translateY(0);}',
            '.ty-popup-skip{right:18px;bottom:64px;cursor:pointer;}',
            '.ty-popup-skip::after{content:"";display:inline-block;width:0;height:0;border-style:solid;border-width:6px 0 6px 10px;border-color:transparent transparent transparent #fff;}',
            '.ty-popup-skip:hover{background:rgba(255,255,255,.92);color:#000;}',
            '.ty-popup-skip:hover::after{border-color:transparent transparent transparent #000;}',
            '.ty-popup-link{right:18px;top:18px;cursor:pointer;text-decoration:none;color:#fff;}',
            '.ty-popup-link:hover{background:rgba(255,255,255,.92);color:#000;}',
            '.ty-popup-link .ty-popup-url{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:240px;}',
            '.ty-popup-text{right:18px;top:18px;font-weight:500;}',
            /* up-next: overlay centrato nel player */
            '.ty-upnext{position:absolute;inset:0;z-index:30;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.65);}',
            '.ty-upnext-card{display:flex;flex-direction:column;align-items:center;gap:12px;max-width:min(320px,86%);padding:18px 22px;border-radius:14px;background:rgba(20,20,22,.92);color:#fff;text-align:center;font-family:inherit;}',
            '.ty-upnext-head{font-size:12px;letter-spacing:.4px;text-transform:uppercase;opacity:.7;}',
            '.ty-upnext-thumb{width:120px;max-width:60%;aspect-ratio:16/9;object-fit:cover;border-radius:8px;}',
            '.ty-upnext-title{font-size:14px;font-weight:600;line-height:1.3;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}',
            '.ty-upnext-ring{position:relative;width:64px;height:64px;border:0;padding:0;background:transparent;cursor:pointer;}',
            '.ty-upnext-ring svg{position:absolute;inset:0;transform:rotate(-90deg);}',
            '.ty-upnext-ring .ty-upnext-ring-bg{fill:none;stroke:rgba(255,255,255,.25);stroke-width:3;}',
            '.ty-upnext-ring .ty-upnext-ring-fg{fill:none;stroke:#fff;stroke-width:3;stroke-linecap:round;}',
            '.ty-upnext-ring .ty-upnext-playicon{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;font-size:0;}',
            '.ty-upnext-ring .ty-upnext-playicon::before{content:"";display:block;width:0;height:0;border-style:solid;border-width:11px 0 11px 18px;border-color:transparent transparent transparent #fff;margin-left:4px;}',
            '.ty-upnext-actions{display:flex;gap:10px;}',
            '.ty-upnext-actions button{border:0;border-radius:999px;padding:8px 16px;font-size:13px;font-weight:600;cursor:pointer;}',
            '.ty-upnext-btn-cancel{background:rgba(255,255,255,.15);color:#fff;}',
            '.ty-upnext-btn-play{background:#fff;color:#000;}',
            /* mobile: variante MINIMALE — niente thumb né etichetta, anello
               piccolo, titolo su una riga, bottoni compatti */
            '@media (max-width:600px){',
            '.ty-upnext{background:rgba(0,0,0,.55);}',
            '.ty-upnext-card{gap:8px;padding:12px 16px;max-width:88%;background:rgba(20,20,22,.85);}',
            '.ty-upnext-head{display:none;}',
            '.ty-upnext-thumb{display:none;}',
            '.ty-upnext-title{font-size:12.5px;-webkit-line-clamp:1;}',
            '.ty-upnext-ring{width:46px;height:46px;}',
            '.ty-upnext-ring .ty-upnext-playicon::before{border-width:8px 0 8px 13px;margin-left:3px;}',
            '.ty-upnext-actions button{padding:6px 12px;font-size:12px;}',
            '}'
        ].join('\n');
        document.head.appendChild(style);
    }

    // ------------------------------------------------------------------
    // Ads IMA (google.ima, SDK ima3.js) — integrazione vanilla senza
    // plugin v8. Pre-roll al primo play; il contenuto viene messo in
    // pausa/ripreso dagli eventi IMA e riparte SEMPRE se gli ads
    // falliscono o finiscono (mai bloccare il playback).
    // ------------------------------------------------------------------
    function setupImaAds(player, mediaEl, uiHost, adTagUrl) {
        if (!(window.google && google.ima)) { return; } // SDK assente/bloccato: si riproduce senza ads
        if (!uiHost) { return; }
        injectCoreStyles();

        var videoEl = innerVideoEl(mediaEl) || mediaEl;

        var adDiv = document.createElement('div');
        adDiv.className = 'ty-ad-container';
        uiHost.appendChild(adDiv);

        var displayContainer = new google.ima.AdDisplayContainer(adDiv, videoEl);
        var adsLoader = new google.ima.AdsLoader(displayContainer);
        var adsManager = null;
        var adActive = false;
        var adPaused = false;

        // bottone play grande al centro: compare quando l'ad è in pausa
        var adResumeBtn = document.createElement('button');
        adResumeBtn.type = 'button';
        adResumeBtn.className = 'ty-ad-resume';
        adResumeBtn.setAttribute('aria-label', 'Riprendi pubblicità');
        adResumeBtn.innerHTML = '<span></span>';
        adDiv.appendChild(adResumeBtn);

        function setAdPaused(p) {
            adPaused = p;
            adDiv.classList.toggle('ty-ad-paused', !!p);
        }

        function resumeAdIfPaused() {
            if (adsManager && adActive && adPaused) {
                try { adsManager.resume(); } catch (e) {}
            }
        }
        adResumeBtn.addEventListener('click', function (e) {
            e.stopPropagation();
            resumeAdIfPaused();
        });
        // ritorno dal clickthrough (nuova tab/app): riprende la pubblicità
        window.addEventListener('focus', resumeAdIfPaused);
        document.addEventListener('visibilitychange', function () {
            if (!document.hidden) { resumeAdIfPaused(); }
        });
        // Click sull'ad: comportamento STANDARD IMA — tutta l'area è
        // clickthrough (apre il link dell'inserzionista) e l'SDK mette in
        // pausa (ribadito dal listener CLICK); al ritorno il resume arriva
        // da focus/visibilitychange. Nessun layer custom sopra l'ad.

        // gradiente estetico dietro la UI nativa IMA della fascia bassa
        var adBanner = document.createElement('div');
        adBanner.className = 'ty-ad-banner';
        adDiv.appendChild(adBanner);

        function hostSize() {
            var r = uiHost.getBoundingClientRect();
            return { w: Math.max(1, Math.round(r.width)) || 640, h: Math.max(1, Math.round(r.height)) || 360 };
        }

        function resumeContent() {
            adActive = false;
            setAdPaused(false);
            adDiv.classList.remove('ty-ad-active');
            try { mediaEl.play(); } catch (e) {}
        }
        function pauseContent() {
            adActive = true;
            adDiv.classList.add('ty-ad-active');
            try { mediaEl.pause(); } catch (e) {}
        }
        function onAdError() {
            try { if (adsManager) { adsManager.destroy(); } } catch (e) {}
            adsManager = null;
            resumeContent();
        }

        adsLoader.addEventListener(
            google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
            function (loadedEvent) {
                try {
                    // UI NATIVA di IMA nella fascia bassa: badge "Annuncio",
                    // countdown (learn more/skip li rende comunque l'ad)
                    var renderingSettings = new google.ima.AdsRenderingSettings();
                    try {
                        renderingSettings.uiElements = [
                            google.ima.UiElements.AD_ATTRIBUTION,
                            google.ima.UiElements.COUNTDOWN
                        ];
                    } catch (err) {}
                    adsManager = loadedEvent.getAdsManager(videoEl, renderingSettings);
                    adsManager.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, onAdError);
                    adsManager.addEventListener(google.ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED, pauseContent);
                    adsManager.addEventListener(google.ima.AdEvent.Type.CONTENT_RESUME_REQUESTED, resumeContent);
                    adsManager.addEventListener(google.ima.AdEvent.Type.ALL_ADS_COMPLETED, resumeContent);

                    // il click sull'ad (clickthrough o tap sull'area) mette in
                    // PAUSA l'AdsManager: senza resume la pubblicità resterebbe
                    // ferma per sempre. Si riprende sia al tap successivo
                    // sull'ad sia al ritorno del focus dalla clickthrough.
                    adsManager.addEventListener(google.ima.AdEvent.Type.PAUSED, function () {
                        setAdPaused(true);
                    });
                    adsManager.addEventListener(google.ima.AdEvent.Type.RESUMED, function () {
                        setAdPaused(false);
                    });
                    // click sulle zone clickthrough di IMA (apre il link):
                    // l'ad va SEMPRE in pausa; al ritorno riprende da sola
                    // (handler su focus/visibilitychange)
                    adsManager.addEventListener(google.ima.AdEvent.Type.CLICK, function () {
                        try { adsManager.pause(); } catch (err) {}
                    });

                    var s = hostSize();
                    adsManager.init(s.w, s.h, google.ima.ViewMode.NORMAL);
                    adsManager.start();
                } catch (e) {
                    onAdError();
                }
            },
            false
        );
        adsLoader.addEventListener(google.ima.AdErrorEvent.Type.AD_ERROR, onAdError, false);

        window.addEventListener('resize', function () {
            try {
                if (adsManager && adActive) {
                    var s = hostSize();
                    adsManager.resize(s.w, s.h, google.ima.ViewMode.NORMAL);
                }
            } catch (e) {}
        });

        // pre-roll al primo play: il primo 'play' (gesto utente oppure
        // autoplay — se muted le policy sono comunque soddisfatte) inizializza
        // il display container e richiede gli ads
        var requested = false;
        function requestAds() {
            if (requested) { return; }
            requested = true;
            try {
                displayContainer.initialize();
                var req = new google.ima.AdsRequest();
                req.adTagUrl = adTagUrl;
                var s = hostSize();
                req.linearAdSlotWidth = s.w;
                req.linearAdSlotHeight = s.h;
                req.nonLinearAdSlotWidth = s.w;
                req.nonLinearAdSlotHeight = s.h;
                req.setAdWillAutoPlay(true);
                req.setAdWillPlayMuted(!!mediaEl.muted);
                adsLoader.requestAds(req);
            } catch (e) {
                resumeContent();
            }
        }
        mediaEl.addEventListener('play', function onFirstPlay() {
            mediaEl.removeEventListener('play', onFirstPlay);
            requestAds();
        });
    }

    // ------------------------------------------------------------------
    // Up-next — card overlay stile YouTube, identica su mobile e desktop.
    // Countdown circolare attorno a un bottone play, titolo (+ thumbnail),
    // azioni "Riproduci ora" e "Annulla". Layer applicativo slottato nel
    // player: la skin v10 resta stock.
    //
    // TyPlayer.showUpNext(player, {
    //     title, image, seconds (default 10), headText,
    //     playText, cancelText,
    //     onPlay(),   // countdown a zero, click sull'anello o "Riproduci ora"
    //     onCancel()  // "Annulla" (l'overlay si chiude)
    // })
    // ------------------------------------------------------------------
    function showUpNext(player, config) {
        config = config || {};
        var host = player && player._uiHost;
        if (!host) { return null; }
        injectCoreStyles();

        // un solo overlay per player
        var prev = host.querySelector(':scope > .ty-upnext');
        if (prev) { prev.parentNode.removeChild(prev); }

        var seconds = config.seconds || 10;
        var R = 29;
        var CIRC = 2 * Math.PI * R;

        var overlay = document.createElement('div');
        overlay.className = 'ty-upnext';
        overlay.innerHTML =
            '<div class="ty-upnext-card">' +
                '<div class="ty-upnext-head"></div>' +
                (config.image ? '<img class="ty-upnext-thumb" alt="">' : '') +
                '<div class="ty-upnext-title"></div>' +
                '<button type="button" class="ty-upnext-ring" aria-label="Play">' +
                    '<svg viewBox="0 0 64 64" width="64" height="64">' +
                        '<circle class="ty-upnext-ring-bg" cx="32" cy="32" r="' + R + '"></circle>' +
                        '<circle class="ty-upnext-ring-fg" cx="32" cy="32" r="' + R + '" stroke-dasharray="' + CIRC + '" stroke-dashoffset="0"></circle>' +
                    '</svg>' +
                    '<span class="ty-upnext-playicon"></span>' +
                '</button>' +
                '<div class="ty-upnext-actions">' +
                    '<button type="button" class="ty-upnext-btn-cancel"></button>' +
                    '<button type="button" class="ty-upnext-btn-play"></button>' +
                '</div>' +
            '</div>';

        overlay.querySelector('.ty-upnext-head').textContent = config.headText || 'Up next';
        overlay.querySelector('.ty-upnext-title').textContent = config.title || '';
        overlay.querySelector('.ty-upnext-btn-cancel').textContent = config.cancelText || 'Annulla';
        overlay.querySelector('.ty-upnext-btn-play').textContent = config.playText || 'Riproduci ora';
        if (config.image) {
            overlay.querySelector('.ty-upnext-thumb').src = config.image;
        }

        host.appendChild(overlay);

        var ringFg = overlay.querySelector('.ty-upnext-ring-fg');
        var done = false;
        var rafId = null;
        var start = null;

        function dismiss() {
            done = true;
            if (rafId) { cancelAnimationFrame(rafId); }
            if (overlay.parentNode) { overlay.parentNode.removeChild(overlay); }
        }
        function firePlay() {
            if (done) { return; }
            dismiss();
            if (typeof config.onPlay === 'function') { config.onPlay(); }
        }
        function fireCancel() {
            if (done) { return; }
            dismiss();
            if (typeof config.onCancel === 'function') { config.onCancel(); }
        }

        // anello che si svuota in `seconds`; a zero parte il prossimo video
        function tick(ts) {
            if (done) { return; }
            if (start === null) { start = ts; }
            var progress = Math.min(1, (ts - start) / (seconds * 1000));
            ringFg.style.strokeDashoffset = String(CIRC * progress);
            if (progress >= 1) {
                firePlay();
                return;
            }
            rafId = requestAnimationFrame(tick);
        }
        rafId = requestAnimationFrame(tick);

        overlay.querySelector('.ty-upnext-ring').addEventListener('click', firePlay);
        overlay.querySelector('.ty-upnext-btn-play').addEventListener('click', firePlay);
        overlay.querySelector('.ty-upnext-btn-cancel').addEventListener('click', fireCancel);
        // se l'utente fa ripartire il video, l'overlay si toglie da solo
        player.one('play', function () { if (!done) { dismiss(); } });

        return { dismiss: dismiss };
    }

    // Factory: monta il markup v10 stock nel container e ritorna l'adapter.
    function create(config) {
        config = config || {};
        var opts = config.opts || {};

        // preset preview (trailer/hero): autoplay muto in loop, nessuna UI
        if (opts.preview) {
            opts.autoplay = true;
            opts.muted = true;
            opts.loop = true;
            opts.controls = false;
        }
        var controls = opts.controls !== false;

        var container = (typeof config.container === 'string')
            ? document.querySelector(config.container)
            : config.container;

        // media element (+ <video slot="media"> nativo in light DOM).
        // ATTENZIONE ordine di upgrade: con i custom element GIÀ definiti
        // (SPA: ogni apertura dopo la prima) document.createElement esegue
        // il costruttore SUBITO, prima che il <video slot=media> esista —
        // skin/engine si agganciano a un video interno diverso da quello
        // slottato (menu captions vuoto, tracce "fantasma"). Costruendo il
        // sottoalbero via PARSER (fragment) l'upgrade avviene solo alla
        // connessione al DOM, a slotted già presente — lo stesso ordine di
        // una pagina parsata, che è il percorso testato/funzionante.
        var tag = mediaTagForSource(config.type, config.src);
        var mediaEl;
        if (tag === 'video') {
            mediaEl = document.createElement('video');
            mediaEl.setAttribute('webkit-playsinline', '');
        } else {
            mediaEl = document.createRange().createContextualFragment(
                '<' + tag + '><video slot="media" playsinline webkit-playsinline></video></' + tag + '>'
            ).firstChild;
        }
        if (config.id) { mediaEl.id = config.id; }
        mediaEl.setAttribute('playsinline', '');

        if (config.title) { mediaEl.setAttribute('title', config.title); }
        if (config.poster) { mediaEl.setAttribute('poster', config.poster); }
        if (opts.autoplay) { mediaEl.setAttribute('autoplay', ''); }
        if (opts.loop) { mediaEl.setAttribute('loop', ''); }
        if (opts.muted) {
            mediaEl.setAttribute('muted', '');
            // la proprietà viene impostata DOPO la connessione (vedi sotto):
            // sull'elemento non ancora upgradato creerebbe una own property
            // che ombreggia l'accessor della classe
        }
        if (opts.live) { mediaEl.setAttribute('stream-type', 'live'); }
        if (opts.fillsize) {
            // object-fit cover tramite la CSS custom property documentata
            // del media element v10 (API pubblica, non customizzazione skin)
            mediaEl.style.setProperty('--media-object-fit', 'cover');
        }

        // tracce testuali esterne (le tracce nel manifest HLS arrivano da
        // sole); custom-media-element inoltra i <track> al video interno
        (config.subtitles || []).forEach(function (t) {
            if (!t || !t.src) { return; }
            var track = document.createElement('track');
            track.setAttribute('kind', t.kind || 'subtitles');
            track.setAttribute('src', t.src);
            if (t.srclang) { track.setAttribute('srclang', t.srclang); }
            if (t.label) { track.setAttribute('label', t.label); }
            if (t.default) { track.setAttribute('default', ''); }
            mediaEl.appendChild(track);
        });

        // provider (+ skin stock quando i controlli sono attivi)
        var wrapper = document.createElement('video-player');
        var skin = null;
        if (controls) {
            skin = document.createElement('video-skin');
            if (opts.skinClass) { skin.className = opts.skinClass; }
            skin.appendChild(mediaEl);
            wrapper.appendChild(skin);
        } else {
            wrapper.appendChild(mediaEl);
        }
        if (container) { container.appendChild(wrapper); }

        // elemento ormai connesso e upgradato: proprietà sicure da impostare
        if (opts.muted) { try { mediaEl.muted = true; } catch (e) {} }

        var player = adapter(mediaEl, wrapper);
        // host dei layer applicativi del core (ads, up-next): la skin quando
        // c'è (slottata nel contenitore del player), altrimenti il container
        player._uiHost = skin || container;

        // Big play centrale (stesso stile del resume adv) quando l'autoplay
        // viene BLOCCATO dal browser: se poco dopo il caricamento il media è
        // ancora fermo all'inizio, si mostra; il click (gesto utente) avvia.
        // Mai in preview (i trailer sono muted e l'autoplay passa sempre).
        if (!opts.preview) {
            (function () {
                injectCoreStyles();
                var bigPlay = document.createElement('button');
                bigPlay.type = 'button';
                bigPlay.className = 'ty-bigplay';
                bigPlay.setAttribute('aria-label', 'Play');
                bigPlay.innerHTML = '<span></span>';
                player._uiHost.appendChild(bigPlay);

                function hideBigPlay() { bigPlay.classList.remove('ty-bigplay-visible'); }
                mediaEl.addEventListener('play', hideBigPlay);
                mediaEl.addEventListener('playing', hideBigPlay);
                bigPlay.addEventListener('click', function (e) {
                    e.stopPropagation();
                    hideBigPlay();
                    try { mediaEl.play(); } catch (err) {}
                });

                // REGOLA SEMPLICE (scelta owner): il big play è visibile
                // OGNI VOLTA che il contenuto non è in riproduzione — mai
                // sopra un ad attivo o la card up-next.
                function refreshBigPlay() {
                    var v = innerVideoEl(mediaEl) || mediaEl;
                    var adOn = player._uiHost.querySelector('.ty-ad-container.ty-ad-active');
                    var upnextOn = player._uiHost.querySelector('.ty-upnext');
                    if (!adOn && !upnextOn && v.paused) {
                        bigPlay.classList.add('ty-bigplay-visible');
                    } else {
                        hideBigPlay();
                    }
                }
                ['pause', 'ended', 'emptied'].forEach(function (ev) {
                    mediaEl.addEventListener(ev, refreshBigPlay);
                });
                setTimeout(refreshBigPlay, 800);
                // rete di sicurezza leggera (eventi custom element non
                // sempre ri-dispatchati)
                var bpTimer = setInterval(function () {
                    if (!document.body.contains(bigPlay)) { clearInterval(bpTimer); return; }
                    refreshBigPlay();
                }, 1200);
            })();
        }

        // ------------------------------------------------------------------
        // Workaround bug @videojs/html beta.26 (sottotitoli HLS in-manifest):
        // con renderTextTracksNatively:false le tracce native le crea il
        // mixin all'evento NON_NATIVE_TEXT_TRACKS_FOUND, ma il re-setup su
        // MEDIA_ATTACHED le RIMUOVE (track[data-removeondestroy]) e l'evento
        // non viene più riemesso: nel menu resta solo la lingua attiva.
        // Rimedio: se hls conosce sottotitoli ma i <track> non-nativi sono
        // spariti, si ri-emette l'evento — creazione tracce (con gli id che
        // instradano i cue via CUES_PARSED), menu della skin e switch lingua
        // passano TUTTI dalla pipeline ufficiale del mixin.
        // Su native-hls (Safari) le tracce sono già native: non serve.
        // ------------------------------------------------------------------
        if (mediaEl.tagName === 'HLSJS-VIDEO') {
            var tySubsTimer = setInterval(function () {
                if (!document.body.contains(mediaEl)) { clearInterval(tySubsTimer); return; }
                var hls = mediaEl.engine;
                var inner = innerVideoEl(mediaEl);
                if (!hls || !inner || !hls.subtitleTracks || hls.subtitleTracks.length === 0) { return; }
                try {
                    for (var si = 0; si < hls.subtitleTracks.length; si++) {
                        var st = hls.subtitleTracks[si];
                        if (st.forced) { continue; }
                        // id nello schema del mixin: instrada i cue
                        // (CUES_PARSED → getTrackById) e lo switch dal menu
                        var tid = st.default ? 'default' : ('subtitles' + si);
                        if (inner.textTracks.getTrackById && inner.textTracks.getTrackById(tid)) { continue; }
                        var trk = document.createElement('track');
                        trk.kind = 'subtitles';
                        trk.label = st.name;
                        if (st.lang) { trk.srclang = st.lang; }
                        trk.id = tid;
                        if (st.default) { trk.default = true; }
                        trk.setAttribute('data-removeondestroy', '');
                        inner.appendChild(trk);
                        trk.track.mode = 'disabled';
                    }
                    // la lingua già attiva in hls resta selezionata nel menu
                    var cur = hls.subtitleTrack;
                    if (cur >= 0 && hls.subtitleTracks[cur]) {
                        var curId = hls.subtitleTracks[cur].default ? 'default' : ('subtitles' + cur);
                        var curTt = inner.textTracks.getTrackById ? inner.textTracks.getTrackById(curId) : null;
                        if (curTt && curTt.mode === 'disabled') { curTt.mode = 'showing'; }
                    }
                } catch (e) {}
            }, 1200);
        }

        if (config.src) {
            player.src({ type: config.type, src: config.src });
        }

        // ads IMA (mai in modalità preview/trailer)
        if (config.adTag && !opts.preview) {
            setupImaAds(player, mediaEl, player._uiHost, config.adTag);
        }

        return player;
    }

    // ------------------------------------------------------------------
    // Popup in-player (ex videojs-overlay, rifatti): skip / link / testo.
    // Dati come prima: [{type:'skip'|'link'|'text', time, val:[{seconds|url|text}]}]
    // UI nuova: chip "vetro" animate; skip = pillola in basso a destra che
    // salta al termine dell'intervallo; link/testo = chip in alto a destra
    // per ~6s. Guidati da timeupdate, mai visibili sopra gli ads (z-index).
    // ------------------------------------------------------------------
    function openExternal(url) {
        try {
            if (window.Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform() &&
                window.cordova && cordova.plugins && cordova.plugins.browsertabs) {
                cordova.plugins.browsertabs.openUrlInBrowser(url, function () {}, function () {});
                return;
            }
        } catch (e) {}
        window.open(url, '_blank', 'noopener');
    }

    function attachPopups(player, popups) {
        if (!Array.isArray(popups) || popups.length === 0) { return; }
        var host = player._uiHost || (player.el && player.el());
        if (!host) { return; }
        injectCoreStyles();

        var layer = document.createElement('div');
        layer.className = 'ty-popup-layer';
        host.appendChild(layer);

        var items = [];
        popups.forEach(function (popup) {
            try {
                var start = parseInt(popup.time, 10);
                if (isNaN(start)) { return; }
                var v = (popup.val && popup.val[0]) || {};
                var el = document.createElement(popup.type === 'link' ? 'a' : 'div');
                el.className = 'ty-popup ty-popup-' + popup.type;
                var end;

                if (popup.type === 'skip') {
                    end = start + (parseInt(v.seconds, 10) || 0);
                    el.textContent = translate('SKIP');
                    el.addEventListener('click', function () {
                        try { player.currentTime(end); } catch (e) {}
                    });
                } else if (popup.type === 'link') {
                    end = start + 6;
                    el.innerHTML = '<span class="ty-popup-url"></span>';
                    el.querySelector('.ty-popup-url').textContent = v.url || '';
                    el.addEventListener('click', function (e) {
                        e.preventDefault();
                        if (v.url) { openExternal(v.url); }
                    });
                } else if (popup.type === 'text') {
                    end = start + 6;
                    el.textContent = v.text || '';
                } else {
                    return;
                }

                el.style.display = 'none';
                layer.appendChild(el);
                items.push({ el: el, start: start, end: end, shown: false });
            } catch (e) {}
        });

        if (!items.length) { return; }

        player.on('timeupdate', function () {
            var t = player.currentTime();
            items.forEach(function (it) {
                var inRange = (t >= it.start && t < it.end);
                if (inRange && !it.shown) {
                    it.shown = true;
                    it.el.style.display = '';
                    requestAnimationFrame(function () { it.el.classList.add('ty-popup-visible'); });
                } else if (!inRange && it.shown) {
                    it.shown = false;
                    it.el.classList.remove('ty-popup-visible');
                    setTimeout(function () { if (!it.shown) { it.el.style.display = 'none'; } }, 260);
                }
            });
        });
    }

    return {
        create: create,
        adapter: adapter,
        showUpNext: showUpNext,
        attachPopups: attachPopups,
        openExternal: openExternal,
        innerVideoEl: innerVideoEl,
        mediaTagForSource: mediaTagForSource
    };
})();

// alias condiviso usato dai moduli esistenti (glow ambientale in live.js)
function tyInnerVideoEl(mediaEl) { return TyPlayer.innerVideoEl(mediaEl); }
